import { useEffect, useState, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { format } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatShortTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) +
      ' ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso.slice(0, 16);
  }
}

function TooltipCard({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  const positive = (p.pnl || 0) >= 0;
  return (
    <div className="bg-[#13131a] border border-white/10 rounded px-3 py-2 text-xs shadow-xl">
      <div className="text-muted-foreground">{formatShortTime(p.closed_at)}</div>
      <div className="font-medium text-foreground mt-1">
        {p.symbol || 'Start'}
        {p.status && p.status !== 'anchor' && (
          <span className={`ml-1 text-[10px] px-1 rounded ${
            p.status === 'closed_tp' ? 'bg-green-500/20 text-green-400' :
            p.status === 'closed_sl' ? 'bg-red-500/20 text-red-400' :
            'bg-yellow-500/20 text-yellow-400'
          }`}>
            {p.status.replace('closed_', '').toUpperCase()}
          </span>
        )}
      </div>
      {p.status && p.status !== 'anchor' && (
        <div className={`font-mono mt-1 ${positive ? 'text-green-400' : 'text-red-400'}`}>
          {positive ? '+' : ''}${p.pnl?.toFixed(2)}
        </div>
      )}
      <div className="font-mono text-primary mt-0.5">Balance ${p.balance?.toFixed(2)}</div>
    </div>
  );
}

export default function EquityCurveCard({ refreshKey = 0 }) {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/paper-trading/equity-curve?limit=30`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setData(await res.json());
    } catch (e) {
      // silent — dashboard already surfaces errors elsewhere
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  if (loading) return null;
  if (!data || !data.points || data.points.length < 1) return null;

  const start = data.start_balance ?? 1000;
  const current = data.current_balance ?? start;
  const diff = current - start;
  const diffPct = start > 0 ? (diff / start) * 100 : 0;
  const positive = diff >= 0;

  // Prepare chart data — time-based X axis so spacing reflects real elapsed time.
  // Use numeric ms for proper time scale; sort ascending by closed_at to be safe.
  const chartData = data.points
    .map((p) => ({
      ...p,
      ts: p.closed_at ? new Date(p.closed_at).getTime() : null,
    }))
    .filter((p) => p.ts && !Number.isNaN(p.ts))
    .sort((a, b) => a.ts - b.ts);

  // Single-point fallback: pad domain so axis renders nicely
  const tsMin = chartData.length ? chartData[0].ts : Date.now();
  const tsMax = chartData.length > 1 ? chartData[chartData.length - 1].ts : tsMin + 24 * 3600 * 1000;

  return (
    <Card className="p-4 md:p-6 border-border/40 bg-card mb-6" data-testid="equity-curve-card">
      <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-foreground">Equity Curve</h3>
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Last {data.count} closed trades
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            Starting balance ${start.toFixed(2)} · anchored to last reset
          </p>
        </div>
        <div className="text-right">
          <div className="text-xl font-black font-mono text-foreground" data-testid="equity-current">
            ${current.toFixed(2)}
          </div>
          <div className={`text-xs font-mono flex items-center justify-end gap-1 ${positive ? 'text-green-400' : 'text-red-400'}`}>
            {positive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {positive ? '+' : ''}${diff.toFixed(2)} ({positive ? '+' : ''}{diffPct.toFixed(2)}%)
          </div>
        </div>
      </div>

      <div className="h-32 md:h-40 -ml-2">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
            <XAxis
              dataKey="ts"
              type="number"
              scale="time"
              domain={[tsMin, tsMax]}
              tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
              tickFormatter={(t) => format(new Date(t), 'MMM d')}
              minTickGap={28}
              axisLine={false}
              tickLine={false}
              padding={{ left: 8, right: 8 }}
            />
            <YAxis
              domain={['auto', 'auto']}
              tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
              axisLine={false}
              tickLine={false}
              width={48}
              tickFormatter={(v) => `$${v}`}
            />
            <Tooltip content={<TooltipCard />} cursor={{ stroke: 'rgba(255,255,255,0.1)', strokeWidth: 1 }} />
            <ReferenceLine y={start} stroke="rgba(255,255,255,0.15)" strokeDasharray="3 3" />
            <Line
              type="monotone"
              dataKey="balance"
              stroke={positive ? '#10B981' : '#EF4444'}
              strokeWidth={2}
              dot={{ r: 2, fill: positive ? '#10B981' : '#EF4444' }}
              activeDot={{ r: 4 }}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}
